import { useCustom, useGetLocale, useTranslate } from '@refinedev/core';
import { Alert, Button, Card, Input, Space, Typography } from 'antd';
import { useState } from 'react';
import { IDEAS_FIELDS } from '../activate/model';
import { useActivateCommand } from '../activate/useActivateCommand';

/**
 * E41 · PublishActivityLibrary — "Backoffice — Activity library — Publish" (cc-tools-values, chapter "Behavioral Activation — Choose
 * & rank (mobile, E41)", screen e41e0012-0001-4000-8000-000000000030): the curated library behind "Ideas for your values" — about
 * 150 activities across the five life areas, each with a difficulty guess 1–10, EN and RU, tagged with the card-sort values it
 * serves. The library is seeded by the operator's CLI through this same command; a known library ID revises it (the latest
 * publication wins). Catalogue content, no person. Members only (the server's gate: `isBackofficeMember`). An idea without its RU
 * line, a difficulty outside 1–10 or an unknown area is refused by the server with its copy.
 */
type IdeasView = {
  libraryId: string | null;
  ideas: Array<{ ideaId: string | null; text: string; area: string | null; difficulty: number | null }>;
};

export const BackofficeActivityLibrary = () => {
  const getLocale = useGetLocale();
  const locale: 'en' | 'ru' = getLocale()?.startsWith('ru') ? 'ru' : 'en';
  const t = useTranslate() as unknown as (
    k: string,
    o?: Record<string, unknown>,
  ) => string;
  const { run, isLoading } = useActivateCommand();
  const { data, isError, refetch } = useCustom<IdeasView>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'activityIdeas',
      fields: IDEAS_FIELDS,
      variables: {
        language: { name: 'language', type: 'String', value: locale },
      },
    },
  });
  const current = data?.data ?? null;
  const [libraryId, setLibraryId] = useState('');
  const [label, setLabel] = useState('');
  const [citation, setCitation] = useState('');
  const [ideasJson, setIdeasJson] = useState('[]');
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const ready =
    /^[A-Za-z][A-Za-z0-9_-]{1,48}$/.test(libraryId.trim()) &&
    label.trim() &&
    citation.trim();

  const publish = async () => {
    let ideas: unknown;
    try {
      ideas = JSON.parse(ideasJson);
    } catch {
      setMessage({ type: 'error', text: t('backoffice.activityLibrary.notJson') });
      return;
    }
    const res = await run(
      'publishActivityLibrary',
      {
        libraryId: libraryId.trim(),
        label: label.trim(),
        ideas,
        locales: ['en', 'ru'],
        citation: citation.trim(),
      },
      t('backoffice.activityLibrary.failed'),
    );
    if (res.ok) {
      setMessage({ type: 'success', text: t('backoffice.activityLibrary.published') });
      refetch();
    } else {
      setMessage({ type: 'error', text: res.message });
    }
  };

  return (
    <Space
      direction="vertical"
      size="large"
      style={{ width: '100%' }}
      data-testid="backoffice-activity-library"
    >
      <div>
        <Typography.Title level={3} style={{ marginBottom: 0 }}>
          {t('backoffice.activityLibrary.title')}
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t('backoffice.activityLibrary.intro')}
        </Typography.Paragraph>
      </div>
      {isError ? (
        <Alert type="error" showIcon message={t('backoffice.activityLibrary.loadFailed')} />
      ) : null}
      {message ? (
        <Alert
          type={message.type}
          showIcon
          message={message.text}
          closable
          onClose={() => setMessage(null)}
          data-testid={`backoffice-activity-library-message-${message.type}`}
        />
      ) : null}
      <Card
        size="small"
        title={t('backoffice.activityLibrary.current')}
        data-testid="backoffice-activity-library-current"
      >
        {current && current.libraryId ? (
          <Space direction="vertical" size={0}>
            <Typography.Text strong data-testid="backoffice-activity-library-name">
              {current.libraryId}
            </Typography.Text>
            <span data-testid="backoffice-activity-library-count">
              {t('backoffice.activityLibrary.ideas', { count: current.ideas.length })}
            </span>
          </Space>
        ) : (
          <Typography.Text type="secondary">
            {t('backoffice.activityLibrary.empty')}
          </Typography.Text>
        )}
      </Card>
      <Card size="small" title={t('backoffice.activityLibrary.formTitle')}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input
            addonBefore={t('backoffice.activityLibrary.libraryId')}
            data-testid="backoffice-activity-library-id"
            onChange={(e) => setLibraryId(e.target.value)}
            value={libraryId}
          />
          <Input
            addonBefore={t('backoffice.activityLibrary.label')}
            data-testid="backoffice-activity-library-label"
            onChange={(e) => setLabel(e.target.value)}
            value={label}
          />
          <Input
            addonBefore={t('backoffice.activityLibrary.citation')}
            data-testid="backoffice-activity-library-citation"
            onChange={(e) => setCitation(e.target.value)}
            value={citation}
          />
          <Typography.Text>{t('backoffice.activityLibrary.ideasJson')}</Typography.Text>
          <Input.TextArea
            autoSize={{ minRows: 4, maxRows: 12 }}
            data-testid="backoffice-activity-library-ideas"
            onChange={(e) => setIdeasJson(e.target.value)}
            value={ideasJson}
          />
          <div>
            <Button
              type="primary"
              disabled={!ready}
              loading={isLoading}
              onClick={() => {
                publish().catch(() => undefined);
              }}
              data-testid="backoffice-activity-library-publish"
            >
              {t('backoffice.activityLibrary.publish')}
            </Button>
          </div>
        </Space>
      </Card>
    </Space>
  );
};
